import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2, Users, GraduationCap } from 'lucide-react';
import { OrgReportsApi } from '@/services/org-reports.api';
import { OrgPortalApi } from '@/services/org.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';
import { cn } from '@/lib/cn';

type Roster = 'students' | 'staff';

const ROSTERS: { key: Roster; label: string; description: string; icon: typeof Users }[] = [
  { key: 'students', label: 'Students', description: 'Full student roster — admission, class/section, guardian details.', icon: GraduationCap },
  { key: 'staff', label: 'Staff', description: 'Full staff roster — employee ID, designation, department, contact.', icon: Users },
];

/**
 * Data Export — org tier. Mirrors school-tier's DataExportPage exactly
 * (Students/Staff toggle, CSV/Excel, no other filters) with the one
 * addition every other org-tier report in this console already has: an
 * optional School narrow (omitted = every school in scope — Org Admin's
 * own org, Super Admin everything). See OrgReportsService's
 * studentsForDataExport()/staffForDataExport() for the real-scale check
 * behind why this is a single bounded export, not chunked/streamed.
 */
export function OrgDataExportPage() {
  const [roster, setRoster] = useState<Roster>('students');
  const [schoolId, setSchoolId] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-data-export-filter'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const run = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      const params = { schoolId: schoolId || undefined };
      await (roster === 'students'
        ? OrgReportsApi.exportDataExportStudents(format, params)
        : OrgReportsApi.exportDataExportStaff(format, params));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader title="Data Export" description="Download a full roster as CSV or Excel, across your schools." />

      <div className="mb-4 max-w-xs">
        <label className="mb-1 block text-xs font-medium text-slate-600">School</label>
        <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="grid max-w-xl grid-cols-2 gap-3">
        {ROSTERS.map(({ key, label, description, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setRoster(key)}
            className={cn(
              'rounded-lg border p-4 text-left transition-colors',
              roster === key ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500' : 'border-slate-200 bg-white hover:bg-slate-50',
            )}
          >
            <Icon className={cn('mb-2 h-5 w-5', roster === key ? 'text-brand-600' : 'text-slate-400')} />
            <div className="text-sm font-semibold text-slate-900">{label}</div>
            <div className="mt-1 text-xs text-slate-500">{description}</div>
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => run('csv')}
          disabled={!!busy}
        >
          {busy === 'csv' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4 text-slate-600" />}
          Download CSV
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => run('xlsx')}
          disabled={!!busy}
        >
          {busy === 'xlsx' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1.5 h-4 w-4" />}
          Download Excel
        </button>
      </div>
    </>
  );
}
